/** @format */

import React, { useEffect } from 'react';
import { Form, Input, Button, InputNumber, Spin, message, Modal } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useListedLeadForm } from '../hooks/useListedLeadForm';
import { toast } from 'react-toastify';

interface ListedLeadsFormProps {
  onSuccess?: () => void;
  visible?: boolean;
  onCancel?: () => void;
}

/**
 * Listed Leads Form Component
 * Batch form for adding/creating listed leads
 */
export const ListedLeadsForm: React.FC<ListedLeadsFormProps> = ({ onSuccess, visible = false, onCancel }) => {
  const selectedOfficeId = useSelector((state: any) => state.dropDown?.officeId);
  const {
    listedLeads,
    loading,
    error,
    success,
    addListedLead,
    removeListedLead,
    updateListedLead,
    submitListedLeads,
    clearMessage,
  } = useListedLeadForm();

  const [form] = Form.useForm();

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearMessage();
    }
  }, [error, clearMessage]);

  useEffect(() => {
    if (success) {
      toast.success('Listed leads added successfully!');
      form.resetFields();
      onSuccess?.();
      onCancel?.();
      clearMessage();
    }
  }, [success, form, onSuccess, onCancel, clearMessage]);

  // Update listed leads with officeId whenever selectedOfficeId changes
  useEffect(() => {
    if (selectedOfficeId && listedLeads.length > 0) {
      listedLeads.forEach((listedLead, index) => {
        if (listedLead.officeId !== selectedOfficeId) {
          updateListedLead(index, {
            ...listedLead,
            officeId: selectedOfficeId,
          });
        }
      });
    }
  }, [selectedOfficeId, listedLeads, updateListedLead]);

  const handleSubmit = async () => {
    const isValid = await form.validateFields();
    if (isValid) {
      const result = await submitListedLeads();
      if (result) {
        message.success('Listed leads created successfully');
      }
    }
  };

  return (
    <Modal
      title='Add Listed Leads'
      visible={visible}
      onCancel={onCancel}
      width={1000}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      footer={[
        <Button key='cancel' onClick={onCancel}>
          Cancel
        </Button>,
        <Button key='submit' type='primary' onClick={handleSubmit} loading={loading}>
          Submit Listed Leads
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Form
            form={form}
            layout='vertical'
            onValuesChange={(changedValues, allValues) => {
              // Track form changes and ensure all required fields are set
              listedLeads.forEach((_, index) => {
                const formData = allValues[`listedLead_${index}`];
                if (formData) {
                  updateListedLead(index, {
                    listId: formData.listId || '',
                    listName: formData.listName || '',
                    officeId: selectedOfficeId || '',
                  });
                }
              });
            }}
          >
            {listedLeads.map((listedLead, index) => (
              <Form.Item key={`listedLead_group_${index}`} noStyle>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    marginBottom: '12px',
                  }}
                >
                  <Form.Item
                    name={[`listedLead_${index}`, 'listId']}
                    label='List ID'
                    rules={[{ required: true, message: 'List ID is required' }]}
                    initialValue={listedLead.listId}
                  >
                    <Input placeholder='e.g., LIST001' />
                  </Form.Item>

                  <Form.Item
                    name={[`listedLead_${index}`, 'listName']}
                    label='List Name'
                    rules={[{ required: true, message: 'List Name is required' }]}
                    initialValue={listedLead.listName}
                  >
                    <Input placeholder='e.g., Premium Leads' />
                  </Form.Item>

                  <Form.Item label=' '>
                    <Button
                      type='dashed'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeListedLead(index)}
                      disabled={listedLeads.length === 1}
                    >
                      Remove
                    </Button>
                  </Form.Item>
                </div>
              </Form.Item>
            ))}
          </Form>

          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={addListedLead}
            block
          >
            Add Listed Lead
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default ListedLeadsForm;
